import { useState, useEffect, useCallback } from "react";
import type { Deal as HFDeal } from "../types/deal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle } from "lucide-react";
import { useToast } from "./ui/toast";
import { Checkbox } from "./ui/checkbox";

interface FacilitiesModalProps {
  deal?: HFDeal | null;
  isOpen: boolean;
  onClose: () => void;
  hasRevenueConflicts?: {
    restaurant?: boolean;
    bar?: boolean;
    spa?: boolean;
  };
  facilitiesArray: string[];
  onSaveArray: (facilities: string[]) => void;
}

export const FACILITY_KEY_MAP: Record<string, string> = {
  roomservice: "roomService",
  room_service: "roomService",
  "room service": "roomService",
  restaurant: "restaurant",
  bar: "bar",
  spa: "spa",
  parking: "parking",
  meetings: "meetings",
  meeting: "meetings",
  "meeting & events": "meetings",
  meetingevents: "meetings",
  pool: "pool",
  gym: "gym",
  "gym / fitness": "gym",
  gymfitness: "gym",
};

function normalizeFacilityKey(key: string): string {
  const lower = key.toLowerCase().trim();
  return FACILITY_KEY_MAP[lower] ?? lower;
}

const facilityConfig = [
  {
    key: "restaurant",
    label: "Restaurant",
    helper: "Enables Breakfast/Lunch/Dinner in F&B Revenue.",
  },
  {
    key: "bar",
    label: "Bar",
    helper: "Enables Bar in F&B Revenue.",
  },
  {
    key: "roomService",
    label: "Room Service",
    helper: "Provides guests with in-room dining options.",
  },
  {
    key: "spa",
    label: "Spa",
    helper: "Enables Spa in Other Revenue.",
  },
  {
    key: "parking",
    label: "Parking",
    helper: "Referenced in Other Revenue checks and capacity calculations.",
  },
  {
    key: "meetings",
    label: "Meeting & Events",
    helper: "Referenced in Other Revenue checks and space utilization.",
  },
  {
    key: "pool",
    label: "Pool",
    helper: "A key leisure amenity for guest satisfaction.",
  },
  {
    key: "gym",
    label: "Gym / Fitness Center",
    helper: "Essential for guest wellness and modern hotel standards.",
  },
];

export default function FacilitiesModal({
  deal,
  isOpen,
  onClose,
  hasRevenueConflicts = {},
  facilitiesArray,
  onSaveArray,
}: FacilitiesModalProps) {
  const { toast } = useToast();

  const [localFacilitiesArray, setLocalFacilitiesArray] = useState<string[]>(
    () => (facilitiesArray || []).map(normalizeFacilityKey),
  );

  useEffect(() => {
    if (isOpen) {
      setLocalFacilitiesArray(
        (facilitiesArray || []).map(normalizeFacilityKey),
      );
    }
  }, [isOpen, facilitiesArray]);

  const handleToggleArray = (key: string) => {
    setLocalFacilitiesArray((prev) => {
      const isPresent = prev.includes(key);
      if (isPresent) {
        return prev.filter((f) => f !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleSave = () => {
    onSaveArray(localFacilitiesArray);
    toast.success("Facilities updated");
    onClose();
  };

  const handleCancel = () => {
    setLocalFacilitiesArray((facilitiesArray || []).map(normalizeFacilityKey));
    onClose();
  };

  const getConflictWarnings = useCallback(() => {
    const warnings = [];
    const isActive = (key: string) => localFacilitiesArray.includes(key);

    if (!isActive("restaurant") && hasRevenueConflicts.restaurant) {
      warnings.push(
        "You've turned off **Restaurant** while F&B meal revenue is entered. You can keep the inputs, but we'll flag a warning on the F&B page.",
      );
    }
    if (!isActive("bar") && hasRevenueConflicts.bar) {
      warnings.push(
        "You've turned off **Bar** while Bar revenue is entered. You can keep the inputs, but we'll flag a warning on the F&B page.",
      );
    }
    if (!isActive("spa") && hasRevenueConflicts.spa) {
      warnings.push(
        "You've turned off **Spa** while Spa revenue is entered. You can keep the inputs, but we'll flag a warning on the Other Revenue page.",
      );
    }

    return warnings;
  }, [localFacilitiesArray, hasRevenueConflicts]);

  const conflictWarnings = getConflictWarnings();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-8 flex flex-col">
        <DialogHeader className="mb-6">
          <DialogTitle>Configure Property Facilities</DialogTitle>
          <p className="text-base text-slate-500 font-normal">
            Select the key facilities and amenities available at this hotel
            property. Changes may impact revenue forecasting sections.
          </p>
        </DialogHeader>

        <div className="flex-grow overflow-y-auto space-y-6 pr-4 -mr-4">
          {conflictWarnings.length > 0 && (
            <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-4 shadow-md transition-all">
              <div className="flex items-start space-x-3 mb-2">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-base font-bold">
                  Revenue Conflict Alert: Review Inputs
                </p>
              </div>
              <ul className="list-disc space-y-1 pl-8 text-amber-800 text-sm">
                {conflictWarnings.map((warning, index) => (
                  <li key={index} className="leading-snug">
                    <span
                      dangerouslySetInnerHTML={{
                        __html: warning.replace(
                          /\*\*(.*?)\*\*/g,
                          '<span class="font-semibold">$1</span>',
                        ),
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-4">
            {facilityConfig.map((facility) => {
              const checked = localFacilitiesArray.includes(facility.key);

              return (
                <div
                  key={facility.key}
                  className="flex items-start space-x-4 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <Checkbox
                    id={facility.key}
                    checked={checked}
                    onCheckedChange={() => handleToggleArray(facility.key)}
                    className="mt-1 flex-shrink-0"
                  />
                  <label
                    htmlFor={facility.key}
                    className="cursor-pointer space-y-0.5"
                  >
                    <span className="text-base font-semibold text-slate-800">
                      {facility.label}
                    </span>
                    {facility.helper && (
                      <p className="text-sm text-slate-500">
                        {facility.helper}
                      </p>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="pt-6 mt-6 border-t border-slate-300">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-primary-blue hover:bg-primary-blue"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
